<template>
  <!-- Carril «Fuentes»: las fuentes de la definición; la posición es la prioridad (P1 arriba). Solo emite: el padre
       aplica cada cambio con deshacer (salvo el texto fijo de origen, que no tiene deshacer propio). -->
  <aside ref="rootRef" class="sr" :aria-label="t('src.title')">
    <div class="sr-head">
      <span class="mc-ico form" aria-hidden="true">F</span>
      <span class="sr-title">{{ t('src.title') }}</span>
      <span class="sr-count">{{ sources.length }}</span>
    </div>

    <div ref="addBoxRef" class="sr-addbox" @keydown.esc="onAddEsc">
      <button ref="addBtnRef" type="button" class="btn sr-add" aria-haspopup="dialog" :aria-expanded="adding"
              @click="adding ? closeAdd(false) : openAdd()">{{ t('src.add') }}</button>
      <div v-if="adding" class="sr-pop" role="dialog" :aria-label="t('src.addTitle')">
        <div class="sr-pop-head">{{ t('src.addTitle') }}</div>
        <PickList :options="addOptions" :empty-text="t('src.addNone')" autofocus @pick="onAdd" @close="closeAdd(true)" />
      </div>
    </div>

    <input v-if="compact" v-model="filter" class="in sr-filter" :placeholder="t('src.filter')" :aria-label="t('src.filter')" />
    <p v-if="!sources.length" class="hint sr-empty">{{ t('src.empty') }}</p>

    <p v-if="sources.length > 1" class="hint sr-order">{{ t('src.orderHint') }}</p>
    <TransitionGroup tag="div" name="vem-list" class="sr-list">
      <div v-for="c in shown" :key="c.s.questId" class="sr-card" :data-src="c.s.questId" :style="{ '--fc': c.badge.color }"
           :class="{ open: open === c.s.questId, sel: selected === c.s.questId, off: !c.s.enabled, missing: !c.form, hl: highlight && highlight.has(c.s.questId),
                     dragging: drag && drag.from === c.i, 'drop-before': dropAt(c.i) === 'before', 'drop-after': dropAt(c.i) === 'after' }"
           @click="emit('select', c.s.questId)" @mouseenter="emit('hover', c.s.questId)" @mouseleave="emit('hover', null)"
           @dragover="onDragOver($event, c.i)" @drop="onDrop($event)">
        <div class="sr-card-head">
          <span class="sr-grip" :class="{ off: sources.length < 2 }" :draggable="sources.length > 1" :title="t('src.dragTitle')" aria-hidden="true"
                @dragstart="onDragStart($event, c.i)" @dragend="drag = null" />
          <button type="button" class="sr-card-btn" :aria-pressed="selected === c.s.questId" :title="c.s.questId"
                  @click="emit('select', c.s.questId)">
            <span class="sr-prio mono" :title="t('src.prioTitle', { n: c.i + 1 })">P{{ c.i + 1 }}</span>
            <span class="fbadge sr-badge" :style="{ '--fc': c.badge.color }" :title="t('src.badge', { code: c.badge.code })">{{ c.badge.code }}</span>
            <span class="sr-name">{{ c.name }}</span>
          </button>
          <button type="button" class="sr-switch" role="switch" :aria-checked="c.s.enabled" :aria-label="t('ins.sourceEnabled')"
                  :title="t('ins.sourceEnabled')" @click="patch(c.s, { enabled: !c.s.enabled })">
            <span class="tgl-track"><span class="tgl-knob" /></span>
          </button>
          <button type="button" class="sr-fold" :class="{ on: open === c.s.questId }" :aria-expanded="open === c.s.questId"
                  :aria-label="t(open === c.s.questId ? 'src.collapse' : 'src.expand')" :title="t(open === c.s.questId ? 'src.collapse' : 'src.expand')"
                  @click.stop="emit('toggle', c.s.questId)">
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M3 4.5 6 7.5 9 4.5" /></svg>
          </button>
        </div>
        <div class="sr-meta">
          <template v-if="!compact">
            <span class="mono sr-id">{{ c.s.questId }}</span>
            <span v-if="c.form">{{ t('pal.responses', { n: fmt(c.form.responses) }) }}</span>
            <dl class="sr-key">
              <template v-if="c.prefix">
                <dt>{{ t('src.prefix') }}</dt>
                <dd :title="t('src.prefixTitle')"><span class="sr-ar" /><span class="mono">{{ c.prefix }}</span></dd>
              </template>
              <dt>{{ t(isEntity(c.s) ? 'src.keyEntity' : 'src.keyId') }}</dt>
              <dd :class="{ 'warn-tx': !c.s.idAlias }" :title="c.s.idAlias ? aliasText(c.s.questId, c.s.idAlias) : null">
                <span class="sr-ar">{{ c.s.idAlias ? '←' : '' }}</span><span v-if="c.s.idAlias" class="mono">{{ shortAlias(c.s.questId, c.s.idAlias) }}</span><template v-else>{{ t('list.keyMissing') }}</template>
              </dd>
              <template v-if="!isEntity(c.s)">
                <dt>{{ t('src.keyOrigin') }}</dt>
                <dd :class="{ 'warn-tx': !c.s.originAlias && !hasText(c.s) }" :title="c.s.originAlias ? aliasText(c.s.questId, c.s.originAlias) : null">
                  <span class="sr-ar">{{ c.s.originAlias || hasText(c.s) ? '←' : '' }}</span><span v-if="c.s.originAlias" class="mono">{{ shortAlias(c.s.questId, c.s.originAlias) }}</span>{{ originText(c.s) }}
                </dd>
              </template>
            </dl>
          </template>
          <span v-if="!c.form" class="sr-warn">{{ t('src.missing') }}</span>
          <span v-else-if="!c.s.enabled" class="sr-note">{{ t('src.off') }}</span>
          <span v-else-if="m" class="sr-cov" :class="{ 'warn-tx': c.k < m }" :title="t('src.coverageTitle', { k: c.k, m })">
            {{ t('src.coverage', { k: c.k, m }) }}<span v-if="c.k < m" aria-hidden="true"> ▲</span>
          </span>
        </div>

        <Transition name="vem-fold">
          <div v-if="open === c.s.questId" class="sr-body">
            <div class="sr-sec">{{ t('ins.keyFields') }}</div>
            <div class="seg full sr-mode" role="radiogroup" :aria-label="t('ins.keyMode')">
              <button v-for="km in ['origin', 'entity']" :key="km" type="button" role="radio" :aria-checked="keyMode(c.s) === km"
                      :class="{ on: keyMode(c.s) === km }" @click="patch(c.s, { keyMode: km })">{{ t('ins.keyMode.' + km) }}</button>
            </div>
            <label class="fld">{{ t(isEntity(c.s) ? 'ins.entityIdAlias' : 'ins.idAlias') }}
              <select class="in" data-focus="idAlias" :value="c.s.idAlias || ''" @change="patch(c.s, { idAlias: $event.target.value || null })">
                <option value="">{{ t('ins.chooseAlias') }}</option>
                <option v-for="a in c.aliases" :key="a.alias" :value="a.alias">{{ a.text }}</option>
              </select>
            </label>
            <template v-if="!isEntity(c.s)">
            <label class="fld">{{ t('ins.originAlias') }}
              <select class="in" data-focus="origin" :value="c.s.originAlias || ''" @change="patch(c.s, { originAlias: $event.target.value || null })">
                <option value="">{{ t('ins.originNone') }}</option>
                <option v-for="a in c.aliases" :key="a.alias" :value="a.alias">{{ a.text }}</option>
              </select>
            </label>
            <label class="fld">{{ t('ins.originText') }}
              <input class="in" :value="c.s.originText || ''" :placeholder="t('ins.originTextPh')"
                     @change="patch(c.s, { originText: $event.target.value.trim() || null })" />
            </label>
            <p class="hint">{{ c.s.originAlias ? t('ins.originTextFallback') : t('ins.originTextHint') }}</p>
            </template>
            <p class="hint">{{ t(isEntity(c.s) ? 'ins.entityKeyHint' : 'ins.keyHint') }}</p>
            <template v-if="sources.length > 1">
              <div class="sr-sec">{{ t('ins.priority') }} · P{{ c.i + 1 }}</div>
              <div class="seg full">
                <button type="button" :disabled="c.i === 0" @click="move(c.i, c.i - 1)">{{ t('src.up') }}</button>
                <button type="button" :disabled="c.i === sources.length - 1" @click="move(c.i, c.i + 1)">{{ t('src.down') }}</button>
              </div>
              <p class="hint">{{ t('ins.priorityHint') }}</p>
            </template>
            <button type="button" class="btn danger full" @click="emit('remove', c.s.questId)">{{ t('ins.removeSource') }}</button>
          </div>
        </Transition>
      </div>
    </TransitionGroup>
  </aside>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import PickList from '@vem/components/PickList.vue';
import { locale, t } from '@vem/i18n.js';
import { aliasLabel, catalog, formAliasSets, formById, formName } from '@vem/catalog.js';
import { commonPrefix, formBadge } from '@vem/canvasModel.js';

const FILTER_FROM = 8;
const LOCALES = { es: 'es-ES', ca: 'ca-ES', en: 'en-GB' };

const props = defineProps({
  /** dto.sources: [{questId, idAlias, originAlias, originText, priority, enabled}] (no se muta). */
  sources: { type: Array, required: true },
  /** Alias conectados en dto.maps (los de sistema «@…» se ignoran): Set o array. */
  connected: { type: [Set, Array], default: () => new Set() },
  /** questId de la tarjeta desplegada. */
  open: { type: String, default: null },
  /** questId del formulario cuyas preguntas muestra el lienzo. */
  selected: { type: String, default: null },
  /** questIds marcados (pastilla bajo el puntero, «Ver qué formularios la traen»). */
  highlight: { type: Set, default: null },
});
const emit = defineEmits(['toggle', 'select', 'hover', 'update', 'add', 'move', 'remove']);

const rootRef = ref(null);
const addBoxRef = ref(null);
const addBtnRef = ref(null);
const adding = ref(false);
const filter = ref('');

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString(LOCALES[locale.value] || 'es-ES'));
const fold = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const hasText = (s) => !!(s.originText || '').trim();
const keyMode = (s) => (s.keyMode === 'entity' ? 'entity' : 'origin');
const isEntity = (s) => keyMode(s) === 'entity';

/** Alias de formulario conectados en la definición (sin los de sistema). */
const connectedSet = computed(() => new Set([...props.connected].filter((a) => a && !a.startsWith('@'))));
const m = computed(() => connectedSet.value.size);
const overlap = (questId) => {
  const set = formAliasSets.value.get(questId);
  let k = 0;
  if (set) connectedSet.value.forEach((a) => { if (set.has(a)) k += 1; });
  return k;
};

const prefixOf = (questId) => commonPrefix((formById(questId)?.aliases || []).map((a) => a.alias));
const shortAlias = (questId, alias) => {
  const p = prefixOf(questId);
  return p && alias.startsWith(p) ? alias.slice(p.length) : alias;
};
const aliasText = (questId, alias) => {
  const l = aliasLabel(alias, [questId]);
  return l ? `${l} · ${shortAlias(questId, alias)}` : shortAlias(questId, alias);
};
/** Texto del origen tras el alias (si lo hay): el texto fijo o su uso como respaldo, o el aviso de que falta. */
function originText(s) {
  if (s.originAlias) return hasText(s) ? ` · ${t('list.keyFallback', { v: s.originText.trim() })}` : '';
  return hasText(s) ? t('key.fixed', { v: s.originText.trim() }) : t('list.originMissing');
}

// El orden del array es la prioridad (el padre lo normaliza al cargar y al mover).
const cards = computed(() => props.sources
  .map((s, i) => {
    const form = formById(s.questId);
    return {
      s,
      i,
      form,
      name: form ? formName(s.questId) : s.questId,
      badge: formBadge(props.sources, s.questId),
      prefix: prefixOf(s.questId),
      k: overlap(s.questId),
      aliases: (form ? form.aliases : []).map((a) => ({ alias: a.alias, text: aliasText(s.questId, a.alias) })),
    };
  }));

const compact = computed(() => props.sources.length > FILTER_FROM);

const shown = computed(() => {
  const q = compact.value ? fold(filter.value.trim()) : '';
  return q ? cards.value.filter((c) => fold(`${c.name} ${c.s.questId}`).includes(q)) : cards.value;
});

function move(from, to) {
  if (to < 0 || to >= props.sources.length || to === from) return;
  emit('move', { from, to });
}

// Arrastrar por el asa: la tarjeta cae antes o después de la que está bajo el puntero (según la mitad).
const drag = ref(null);
const dropAt = (i) => (drag.value && drag.value.over === i && drag.value.from !== i ? drag.value.side : null);

function onDragStart(e, i) {
  drag.value = { from: i, over: null, side: null };
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/plain', String(i));
  const card = e.target.closest('.sr-card');
  if (card) e.dataTransfer.setDragImage(card, 16, 16);
}

function onDragOver(e, i) {
  if (!drag.value) return;
  e.preventDefault();
  const r = e.currentTarget.getBoundingClientRect();
  drag.value.over = i;
  drag.value.side = e.clientY < r.top + r.height / 2 ? 'before' : 'after';
}

function onDrop(e) {
  if (!drag.value) return;
  e.preventDefault();
  const { from, over, side } = drag.value;
  drag.value = null;
  if (over == null || over === from) return;
  const at = side === 'before' ? over : over + 1;
  move(from, at > from ? at - 1 : at);
}

// «+ Añadir formulario»: formularios activos que aún no son fuente, por preguntas conectadas que traen y encuestas.
const addOptions = computed(() => {
  const used = new Set(props.sources.map((s) => s.questId));
  return catalog.forms
    .filter((f) => !used.has(f.id))
    .map((f) => ({ f, k: overlap(f.id) }))
    .sort((a, b) => b.k - a.k || (b.f.responses || 0) - (a.f.responses || 0))
    .map(({ f, k }) => ({
      value: f.id,
      label: formName(f.id),
      sub: f.id,
      meta: t('pal.responses', { n: fmt(f.responses) }),
      hint: m.value && k < m.value ? t('src.addOverlap', { k, m: m.value }) : '',
    }));
});

function patch(s, p) {
  emit('update', { questId: s.questId, patch: p });
}

function onAdd(questId) {
  adding.value = false;
  emit('add', questId);
}

function openAdd() {
  adding.value = true;
}

function closeAdd(refocus) {
  if (!adding.value) return;
  adding.value = false;
  if (refocus) nextTick(() => addBtnRef.value?.focus());
}

function onAddEsc(e) {
  if (!adding.value) return;
  e.stopPropagation();
  closeAdd(true);
}

/** Lleva el foco al selector de id ('idAlias') o de origen ('origin') de una fuente (el padre ya la ha desplegado). */
async function focusSource(questId, field) {
  filter.value = '';
  await nextTick();
  await nextTick();
  const card = rootRef.value?.querySelector(`[data-src="${questId}"]`);
  if (!card) return;
  card.scrollIntoView({ block: 'nearest' });
  (card.querySelector(`[data-focus="${field === 'origin' ? 'origin' : 'idAlias'}"]`) || card.querySelector('.sr-card-btn'))?.focus();
}

function onDocDown(e) {
  if (adding.value && addBoxRef.value && !addBoxRef.value.contains(e.target)) adding.value = false;
}

onMounted(() => document.addEventListener('pointerdown', onDocDown));
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocDown));

defineExpose({ openAdd, focusSource });
</script>

<style>
.vem-root .sr {
  flex: 0 0 360px; display: flex; flex-direction: column; gap: 8px; min-height: 0; background: var(--panel);
  border: 1px solid var(--line2); border-radius: 14px; padding: 12px;
}
.vem-root .sr-head { display: flex; align-items: center; gap: 7px; }
.vem-root .sr-head .mc-ico { flex: 0 0 20px; height: 20px; font-size: 9px; border-radius: 5px; }
.vem-root .sr-title { font-family: var(--mono); font-size: 10.5px; letter-spacing: .14em; text-transform: uppercase; color: var(--tx2); }
.vem-root .sr-count { margin-left: auto; font-family: var(--mono); font-size: 11px; color: var(--tx3); }
.vem-root .sr-addbox { position: relative; }
.vem-root .sr-add { width: 100%; min-height: 34px; color: var(--cyan); border-style: dashed; background: none; }
.vem-root .sr-pop {
  position: absolute; z-index: 20; top: calc(100% + 6px); left: 0; width: 320px; max-width: calc(100vw - 32px);
  background: var(--panel); border: 1px solid var(--line2); border-radius: 12px; padding: 10px; box-shadow: 0 14px 36px var(--shadow);
}
.vem-root .sr-pop-head { font-family: var(--mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--tx3); margin: 0 2px 8px; }
.vem-root .sr-filter { width: 100%; min-height: 30px; padding: 4px 8px; }
.vem-root .sr-empty { margin: 2px 2px 0; }
.vem-root .sr-list { position: relative; flex: 1 1 auto; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 7px; padding: 2px 2px 2px 0; }
.vem-root .sr-order { margin: -2px 2px 0; font-size: 11px; }
.vem-root .sr-card {
  position: relative; cursor: pointer; border: 1px solid var(--line2); border-left: 3px solid var(--cyan); border-radius: 9px; padding: 6px 8px 7px;
  background: var(--panel); transition: box-shadow .2s ease, border-color .2s ease, background .2s ease, opacity .2s ease;
}
.vem-root .sr-card.dragging { opacity: .45; }
.vem-root .sr-card.drop-before::before, .vem-root .sr-card.drop-after::after {
  content: ''; position: absolute; left: 0; right: 0; height: 3px; border-radius: 2px; background: var(--cyan); pointer-events: none;
}
.vem-root .sr-card.drop-before::before { top: -6px; }
.vem-root .sr-card.drop-after::after { bottom: -6px; }
.vem-root .sr-grip {
  flex: 0 0 8px; height: 14px; margin: 3px 0 0 -2px; cursor: grab; color: var(--tx3);
  background: radial-gradient(circle, currentColor 1.2px, transparent 1.4px) 0 0 / 4px 4px;
}
.vem-root .sr-grip:hover { color: var(--cyan); }
.vem-root .sr-grip.off { visibility: hidden; }
.vem-root .sr-grip:active { cursor: grabbing; }
.vem-root .sr-card:hover { box-shadow: var(--lift, 0 8px 24px -12px rgba(31, 35, 40, .25)); }
.vem-root .sr-card.open { border-color: var(--mint-bd); border-left-color: var(--cyan); }
.vem-root .sr-card.sel { border-color: var(--fc); border-left-color: var(--fc); box-shadow: 0 0 0 1px var(--fc); }
.vem-root .sr-card.hl { background: var(--cyan-bg); border-color: var(--mint-bd); }
.vem-root .sr-card.off { border-left-color: var(--port); }
.vem-root .sr-card.off .sr-name { color: var(--tx2); }
.vem-root .sr-card.missing { border-color: var(--amber-bd); border-left-color: var(--amber); background: var(--amber-bg, rgba(168, 100, 0, .08)); }
.vem-root .sr-card-head { display: flex; align-items: flex-start; gap: 6px; }
.vem-root .sr-card-btn {
  flex: 1; min-width: 0; display: flex; align-items: flex-start; gap: 6px; background: none; border: 0; padding: 2px 0;
  color: var(--tx); text-align: left; cursor: pointer;
}
.vem-root .sr-badge { flex: 0 0 auto; min-width: 18px; height: 18px; font-size: 10px; margin-top: 1px; }
.vem-root .sr-card.off .sr-badge { opacity: .45; }
.vem-root .sr-prio { flex: 0 0 auto; font-size: 10.5px; font-weight: 600; color: var(--cyan); padding-top: 2px; }
.vem-root .sr-name { flex: 1; min-width: 0; font-size: 12.5px; font-weight: 600; line-height: 1.3; overflow-wrap: anywhere; }
.vem-root .sr-fold {
  flex: 0 0 22px; height: 22px; margin: -1px -4px 0 0; display: grid; place-items: center; border-radius: 6px;
  background: none; border: 1px solid transparent; color: var(--tx3); cursor: pointer; transition: background .15s ease, color .15s ease, border-color .15s ease;
}
.vem-root .sr-fold:hover { background: var(--panel2); border-color: var(--line2); color: var(--tx); }
.vem-root .sr-fold.on { color: var(--cyan); }
.vem-root .sr-fold svg { fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; transition: transform var(--t-move, .26s) var(--ease, ease); }
.vem-root .sr-fold.on svg { transform: rotate(180deg); }
.vem-root .sr-switch { flex: 0 0 auto; background: none; border: 0; padding: 2px 0; cursor: pointer; }
.vem-root .sr-switch .tgl-track { display: block; width: 28px; height: 16px; border-radius: 16px; background: var(--port); position: relative; }
.vem-root .sr-switch .tgl-knob { position: absolute; top: 2px; left: 2px; width: 12px; height: 12px; border-radius: 50%; background: #fff; transition: left .15s; }
.vem-root .sr-switch[aria-checked='true'] .tgl-track { background: var(--teal); }
.vem-root .sr-switch[aria-checked='true'] .tgl-knob { left: 14px; }
/* alineado con el nombre: asa (8) + hueco (6) + P (≈14) + hueco (6) + letra (18) + hueco (6) − ajuste del asa (2) */
.vem-root .sr-meta { display: flex; flex-direction: column; gap: 1px; padding-left: 52px; font-size: 11.5px; color: var(--tx2); }
.vem-root .sr-id { font-size: 10px; color: var(--tx3); }
.vem-root .sr-key { display: grid; grid-template-columns: auto 1fr; align-items: baseline; gap: 1px 6px; margin: 3px 0 0; }
.vem-root .sr-key dt { font-family: var(--mono); font-size: 9px; letter-spacing: .08em; color: var(--lime); }
.vem-root .sr-ar { display: inline-block; width: 16px; color: var(--tx3); }
.vem-root .sr-key dd { margin: 0; min-width: 0; overflow-wrap: anywhere; color: var(--tx); }
.vem-root .sr-cov { font-family: var(--mono); font-size: 10.5px; color: var(--tx3); margin-top: 2px; }
.vem-root .sr-cov.warn-tx { color: var(--amber); }
.vem-root .sr-warn { color: var(--amber); }
.vem-root .sr-note { color: var(--tx3); }
.vem-root .sr-body { border-top: 1px solid var(--line); margin-top: 7px; padding-top: 4px; cursor: default; }
.vem-root .sr-body .fld { margin-top: 8px; }
.vem-root .sr-body .btn.full { margin-top: 10px; min-height: 32px; }
.vem-root .sr-sec { font-family: var(--mono); font-size: 9.5px; letter-spacing: .12em; text-transform: uppercase; color: var(--tx3); margin-top: 10px; }
.vem-root .sr-sec + .seg { margin-top: 6px; }
@media (max-width: 760px) { .vem-root .sr { flex-basis: auto; max-height: 320px; } }
</style>
